<template>
    <div
      class="KoioteValueLabelWrite"
      :style="valueLabelStyle"
    >
      <div
        v-if="!editingValue"
        class="custom-cursor"
      >
        <p
          :id="labelId"
          class="value-container"
          @click="openDialog"
        >
          {{ label }} {{ isAutoMode ? auto_value : value }} {{ units }}
        </p>
      </div>

      <v-dialog v-model="editingValue" max-width="550px">
        <v-card>
          <v-card-title class="text-h6 font-weight-bold">
            {{ title }}
          </v-card-title>

          <v-card-text>
            <!-- Selector de modo -->
            <v-switch
              v-model="localIsAuto"
              label="Modo automático"
              :disabled="!allowed"
              class="mb-4"
            ></v-switch>

            <!-- Modo Manual -->
            <v-row v-if="!localIsAuto" class="mt-2">
              <v-col cols="12">
                <v-card outlined class="pa-3 bg blue lighten-5">
                  <div class="text-caption font-weight-bold primary--text mb-1">CAUDAL MANUAL</div>
                  <div class="text-subtitle-2 mb-1">Valor en <strong>m³/h</strong></div>
                  <div
                    class="value-display pa-2 rounded border-dashed clickable"
                    @click="openKeyboard('caudal')"
                  >
                    {{ localCaudalManual }} m³/h
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Modo Automático -->
            <template v-else>
              <!-- Sección 1: Caudal (m³/h) -->
              <v-row class="mt-2">
                <v-col cols="12">
                  <v-card outlined class="pa-3 bg green lighten-5">
                    <div class="text-caption font-weight-bold success--text mb-1">CAUDAL AUTOMÁTICO</div>
                    <div class="text-subtitle-2 mb-2">Límites en <strong>m³/h</strong></div>
                    <v-row>
                      <v-col cols="3">
                        <div class="text-caption grey--text mb-1">Caudal mínimo</div>
                        <div
                          :class="[
                            'value-display pa-2 rounded border-dashed clickable',
                            { 'success lighten-4': isQmin }
                          ]"
                          @click="openKeyboard('y1')"
                        >
                          {{ localLimiteY1 }} m³/h
                        </div>
                      </v-col>

                      <v-col cols="3">
                        <div class="text-caption grey--text mb-1">Valor actual</div>
                        <div
                          :class="[
                            'value-display d-flex align-center justify-center pa-2 rounded',
                            isQvarminus || isQvarplus ? 'success lighten-4 success--text' : 'grey lighten-3 grey--text text--darken-2'
                          ]"
                        >
                          {{ (auto_value !== null && !isNaN(auto_value)) ? parseFloat(auto_value).toFixed(1) : '—' }} m³/h
                        </div>
                      </v-col>

                      <v-col cols="3">
                        <div class="text-caption grey--text mb-1">Caudal máximo</div>
                        <div
                          :class="[
                            'value-display pa-2 rounded border-dashed clickable',
                            { 'success lighten-4': isQmax }
                          ]"
                          @click="openKeyboard('y2')"
                        >
                          {{ localLimiteY2 }} m³/h
                        </div>
                      </v-col>

                      <!-- Consigna caudal – Caudal -->
                      <v-col cols="3">
                        <div class="d-flex align-center mb-1">
                          <span class="text-caption grey--text">Cons. caudal</span>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                x-small
                                color="grey"
                                class="ml-1"
                                v-bind="attrs"
                                v-on="on"
                              >
                                mdi-information-outline
                              </v-icon>
                            </template>
                            <span>
                              • Consigna caudal VEGA si Nivel actual VEGA &lt; Nivel mínimo VEGA<br>
                              • Consigna caudal VEGA si Nivel actual VEGA &gt; Nivel mínimo VEGA y Nivel actual 200k &lt; Nivel mínimo 200k<br>
                              • Consigna caudal 200kL si Nivel actual VEGA &gt; Nivel mínimo VEGA y Nivel actual 200k &gt; Nivel mínimo 200k
                            </span>
                          </v-tooltip>
                        </div>
                        <div class="value-display d-flex align-center justify-center pa-2 rounded success lighten-4">
                          <template v-if="isQmin">
                            <span class="font-weight-bold red--text">MIN</span>
                          </template>
                          <template v-else-if="isQmax">
                            <span class="font-weight-bold red--text">MAX</span>
                          </template>
                          <template v-else>
                            <v-icon v-if="isQvarplus" color="green" size="28">mdi-trending-up</v-icon>
                            <v-icon v-if="isQvarminus" color="orange" size="28">mdi-trending-down</v-icon>
                            <v-icon v-if="!isQvarplus && !isQvarminus" size="28">mdi-minus</v-icon>
                          </template>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Sección 2: Tanques 200.000 L → % -->
              <v-row class="mt-4">
                <v-col cols="12">
                  <v-card outlined class="pa-3 bg orange lighten-5">
                    <div class="text-caption font-weight-bold orange--text mb-1">TANQUES 200.000 L</div>
                    <div class="text-subtitle-2 mb-2">Nivel en <strong>%</strong></div>
                    <v-row>
                      <v-col cols="3">
                        <div class="text-caption grey--text mb-1">Nivel mínimo</div>
                        <div
                          class="value-display pa-2 rounded border-dashed clickable"
                          @click="openKeyboard('x1200')"
                        >
                          {{ localLimiteX1200 }} %
                        </div>
                      </v-col>

                      <v-col cols="3">
                        <div class="text-caption grey--text mb-1">Valor actual</div>
                        <div class="value-display d-flex align-center justify-center pa-2 rounded grey lighten-3 grey--text text--darken-2">
                          {{ (porcentaje200K !== null && !isNaN(porcentaje200K)) ? porcentaje200K.toFixed(1) : '—' }} %
                        </div>
                      </v-col>

                      <v-col cols="3">
                        <div class="text-caption grey--text mb-1">Nivel máximo</div>
                        <div
                          class="value-display pa-2 rounded border-dashed clickable"
                          @click="openKeyboard('x2200')"
                        >
                          {{ localLimiteX2200 }} %
                        </div>
                      </v-col>

                      <!-- Consigna caudal – Tanque 200K -->
                      <v-col cols="3">
                        <div class="d-flex align-center mb-1">
                          <span class="text-caption grey--text">Cons. caudal</span>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                x-small
                                color="grey"
                                class="ml-1"
                                v-bind="attrs"
                                v-on="on"
                              >
                                mdi-information-outline
                              </v-icon>
                            </template>
                            <span>
                              • MIN: Nivel actual &lt; Nivel mínimo<br>
                              • Ascendente: Nivel entre límites<br>
                              • MAX: Nivel actual &gt; Nivel máximo
                            </span>
                          </v-tooltip>
                        </div>
                        <div class="value-display d-flex align-center justify-center pa-2 rounded orange lighten-4">
                          <template v-if="porcentaje200K == null || isNaN(porcentaje200K)">
                            <v-icon size="28">mdi-minus</v-icon>
                          </template>
                          <template v-else-if="porcentaje200K < limiteX1Value200">
                            <span class="font-weight-bold red--text">MIN</span>
                          </template>
                          <template v-else-if="porcentaje200K > limiteX2Value200">
                            <span class="font-weight-bold red--text">MAX</span>
                          </template>
                          <template v-else>
                            <v-icon color="green" size="28">mdi-trending-up</v-icon>
                          </template>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Sección 3: Tanques VEGA → % -->
              <v-row class="mt-4">
                <v-col cols="12">
                  <v-card outlined class="pa-3 bg deep-purple lighten-5">
                    <div class="text-caption font-weight-bold deep-purple--text mb-1">TANQUES VEGA</div>
                    <div class="text-subtitle-2 mb-2">Nivel en <strong>%</strong></div>
                    <v-row>
                      <v-col cols="3">
                        <div class="text-caption grey--text mb-1">Nivel mínimo</div>
                        <div
                          class="value-display pa-2 rounded border-dashed clickable"
                          @click="openKeyboard('x1vega')"
                        >
                          {{ localLimiteX1Vega }} %
                        </div>
                      </v-col>

                      <v-col cols="3">
                        <div class="text-caption grey--text mb-1">Valor actual</div>
                        <div class="value-display d-flex align-center justify-center pa-2 rounded grey lighten-3 grey--text text--darken-2">
                          {{ (porcentajeVega !== null && !isNaN(porcentajeVega)) ? porcentajeVega.toFixed(1) : '—' }} %
                        </div>
                      </v-col>

                      <v-col cols="3">
                        <div class="text-caption grey--text mb-1">Nivel máximo</div>
                        <div
                          class="value-display pa-2 rounded border-dashed clickable"
                          @click="openKeyboard('x2vega')"
                        >
                          {{ localLimiteX2Vega }} %
                        </div>
                      </v-col>

                      <!-- Consigna caudal – Tanque VEGA -->
                      <v-col cols="3">
                        <div class="d-flex align-center mb-1">
                          <span class="text-caption grey--text">Cons. caudal</span>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                x-small
                                color="grey"
                                class="ml-1"
                                v-bind="attrs"
                                v-on="on"
                              >
                                mdi-information-outline
                              </v-icon>
                            </template>
                            <span>
                              • MAX: Nivel actual &lt; Nivel mínimo<br>
                              • Descendente: Nivel entre límites<br>
                              • MIN: Nivel actual &gt; Nivel máximo
                            </span>
                          </v-tooltip>
                        </div>
                        <div class="value-display d-flex align-center justify-center pa-2 rounded deep-purple lighten-4">
                          <template v-if="porcentajeVega == null || isNaN(porcentajeVega)">
                            <v-icon size="28">mdi-minus</v-icon>
                          </template>
                          <template v-else-if="porcentajeVega < limiteX1ValueVega">
                            <span class="font-weight-bold red--text">MAX</span>
                          </template>
                          <template v-else-if="porcentajeVega > limiteX2ValueVega">
                            <span class="font-weight-bold red--text">MIN</span>
                          </template>
                          <template v-else>
                            <v-icon color="orange" size="28">mdi-trending-down</v-icon>
                          </template>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>
            </template>

            <!-- Teclado numérico -->
            <KoioteNumericKeyboard
              v-if="editingField"
              :original_value="getOriginalValue()"
              :min_value="min_value"
              :max_value="max_value"
              @submit="submitField"
              @cancel="editingField = null"
              :title="getFieldLabel()"
              class="mt-4"
            />
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn text @click="editingValue = false">Cancelar</v-btn>
            <v-btn color="primary" @click="handleSubmit">Guardar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </template>
  
  <script>
  import { Buffer } from "buffer";
  
  import { mapState, mapGetters, mapActions } from "vuex";
  
  import KoioteNumericKeyboard from "@/components/utils/KoioteNumericKeyboard.vue";
  
  export default {
    name: "KoioteAutomaticValueLabel",
  
    components: {
      KoioteNumericKeyboard,
    },
  
    props: {
      input: Object,
    },
  
    data() {
      return {
        editingValue: false,
        labelId: "",

        editingField: null, // 'caudal', 'y1', 'y2'
        localIsAuto: false,
        localCaudalManual: 0,
        localLimiteY1: 0,
        localLimiteY2: 0,
        localLimiteX1Vega: 0,
        localLimiteX2Vega: 0,
        localLimiteX1200: 0,
        localLimiteX2200: 0,
      };
    },
  
    computed: {
      ...mapState(["editing"]),

      isQmin(){
        return parseFloat(this.auto_value) == parseFloat(this.limiteY1Value);
      },
      isQmax(){
        return parseFloat(this.auto_value) == parseFloat(this.limiteY2Value);
      },
      isQvarminus(){
        return !this.isQmin && !this.isQmax && this.porcentaje200K <= this.limiteX1Value200
      },
      isQvarplus(){
        return !this.isQmin && !this.isQmax && this.porcentaje200K > this.limiteX1Value200
      },
  
      valueLabelStyle() {
        return {
          backgroundColor: this.input.customized.background_color,
          color: this.input.customized.foreground_color,
          border: "1px solid " + this.input.customized.border_color,
          width: "100%",
          height: "100%",
          position: "absolute",
          alignContent: "center",
          textAlign: "center",
          whiteSpace: "break-spaces",
          lineHeight: "normal",
        };
      },
  
      connection_type() {
        return this.actualPage().global_item_list.find((gi) => gi.settings.component_type.includes("connection")).settings
          .component_type;
      },
  
      vartable() {
        return this.actualPage().global_item_list.find((gi) => gi.settings.component_type.includes("connection"))
          ?.customized?.vartable;
      },
  
      label() {
        return this.input.customized?.label?.length > 0 ? `${this.input.customized.label}: ` : "";
      },
  
      write_topic_label(){
        return this.input.customized?.write_topic_bomb?.length > 0 ? this.input.customized.write_topic_bomb : null;
      },
  
      units() {
        return this.input.customized?.units?.length > 0 ? `${this.input.customized.units}` : "";
      },
  
      allowed() {
        return this.input.customized?.permission_address?.length > 0
          ? this.vartable?.find((v) => v.formatted_address == this.input.customized.permission_address)?.value || 0
          : true && !this.isManteni();
      },
  
      value() {
        const retrievedValue =
          this.vartable?.find((v) => v.formatted_address == this.input.customized.address)?.value || 0;
  
        const applicableRetrievedValue = retrievedValue == null || isNaN(retrievedValue) ? 0 : retrievedValue;
  
        return typeof applicableRetrievedValue === "boolean"
          ? applicableRetrievedValue
            ? this.input.customized.true_text
            : this.input.customized.false_text
          : this.input?.customized?.datatype == "ENTERO"
          ? Math.round(applicableRetrievedValue)
          : applicableRetrievedValue > 1000
          ? Math.round(applicableRetrievedValue).toFixed(2)
          : (Math.round(applicableRetrievedValue * 100) / 100).toFixed(2);
      },
      auto_value(){
        const retrievedValue =
          this.vartable?.find((v) => v.formatted_address == this.input.customized.auto_address)?.value || 0;
  
        const applicableRetrievedValue = retrievedValue == null || isNaN(retrievedValue) ? 0 : retrievedValue;
  
        return typeof applicableRetrievedValue === "boolean"
          ? applicableRetrievedValue
            ? this.input.customized.true_text
            : this.input.customized.false_text
          : this.input?.customized?.datatype == "ENTERO"
          ? Math.round(applicableRetrievedValue)
          : applicableRetrievedValue > 1000
          ? Math.round(applicableRetrievedValue).toFixed(2)
          : (Math.round(applicableRetrievedValue * 100) / 100).toFixed(2);
      },
  
      max_value() {
        return this.input.customized.max_value;
      },
  
      min_value() {
        return this.input.customized.min_value;
      },
  
      title() {
        return this.input.customized.title;
      },
      isAutoMode() {
        const addr = this.input.customized.is_auto;
        return this.vartable?.find(v => v.formatted_address === addr)?.value === true;
      },

      caudalManualValue() {
        const addr = this.input.customized.caudal_manual;
        return this.vartable?.find(v => v.formatted_address === addr)?.value || 0;
      },

      limiteY1Value() {
        const addr = this.input.customized.limite_caudal_Y1;
        return this.vartable?.find(v => v.formatted_address === addr)?.value || 0;
      },

      limiteY2Value() {
        const addr = this.input.customized.limite_caudal_Y2;
        return this.vartable?.find(v => v.formatted_address === addr)?.value || 0;
      },

      porcentajeVega(){
        const addr = this.input.customized.porcentaje_vega;
        return this.vartable?.find(v => v.formatted_address === addr)?.value || 0;
      },
      limiteX1ValueVega(){
        const addr = this.input.customized.limite_volumen_vega_X1;
        return this.vartable?.find(v => v.formatted_address === addr)?.value || 0;
      },
      limiteX2ValueVega(){
        const addr = this.input.customized.limite_volumen_vega_X2;
        return this.vartable?.find(v => v.formatted_address === addr)?.value || 0;
      },

      porcentaje200K(){
        const addr = this.input.customized.porcentaje_200;
        return this.vartable?.find(v => v.formatted_address === addr)?.value || 0;
      },
      limiteX1Value200(){
        const addr = this.input.customized.limite_volumen_200_X1;
        return this.vartable?.find(v => v.formatted_address === addr)?.value || 0;
      },
      limiteX2Value200(){
        const addr = this.input.customized.limite_volumen_200_X2;
        return this.vartable?.find(v => v.formatted_address === addr)?.value || 0;
      },

    },
  
    methods: {
      ...mapGetters(["actualPage", "isManteni"]),
      ...mapActions("nodered", ["doPublish"]),
  
      generateId() {
        return Math.random().toString(36).slice(2);
      },

      openDialog() {
        if (!this.allowed || this.editing) return;

        this.localIsAuto = this.isAutoMode;

        this.localCaudalManual = this.caudalManualValue;

        this.localLimiteY1 = this.limiteY1Value;
        this.localLimiteY2 = this.limiteY2Value;

        this.localLimiteX1200 = this.limiteX1Value200;
        this.localLimiteX2200 = this.limiteX2Value200;

        this.localLimiteX1Vega = this.limiteX1ValueVega;
        this.localLimiteX2Vega = this.limiteX2ValueVega;

        this.editingValue = true;
      },

      openKeyboard(field) {
        this.editingField = field;
      },

      getOriginalValue() {
        switch (this.editingField) {
          case 'caudal': return this.localCaudalManual;

          case 'y1': return this.localLimiteY1;
          case 'y2': return this.localLimiteY2;

          case 'x1200': return this.localLimiteX1200;
          case 'x2200': return this.localLimiteX2200;

          case 'x1vega': return this.localLimiteX1Vega;
          case 'x2vega': return this.localLimiteX2Vega;

          default: return 0;
        }
      },

      getFieldLabel() {
        switch (this.editingField) {
          case 'caudal': return 'Caudal manual';

          case 'y1': return 'Caudal mínimo';
          case 'y2': return 'Caudal máximo';

          case 'x1200': return 'Nivel 1 Tanques 200.000';
          case 'x2200': return 'Nivel 2 Tanques 200.000';

          case 'x1vega': return 'Nivel 1 Tanques VEGA';
          case 'x2vega': return 'Nivel 2 Tanques VEGA';

          default: return 'Valor';
        }
      },

      submitField(value) {
        switch (this.editingField) {
          case 'caudal': this.localCaudalManual = value; break;

          case 'y1': this.localLimiteY1 = value; break;
          case 'y2': this.localLimiteY2 = value; break;

          case 'x1200': this.localLimiteX1200 = value; break;
          case 'x2200': this.localLimiteX2200 = value; break;

          case 'x1vega': this.localLimiteX1Vega = value; break;
          case 'x2vega': this.localLimiteX2Vega = value; break;
        }
        this.editingField = null;
      },

      handleSubmit() {
        // Escribir is_auto
        this.writeValue(this.input.customized.is_auto, this.localIsAuto ? 1 : 0, true, "Modificado control automático de tanques de 200.000");

        // Escribir valores según el modo
        //modo manual (caudal)
        if (!this.localIsAuto) {
          this.writeValue(this.input.customized.caudal_manual, this.localCaudalManual, false);
        } 
        //modo automatico
        else {
          //caudal
          this.writeValue(this.input.customized.limite_caudal_Y1, this.localLimiteY1, false);
          this.writeValue(this.input.customized.limite_caudal_Y2, this.localLimiteY2, false);

          // porcentaje de tanques de 200k
          this.writeValue(this.input.customized.limite_volumen_200_X1, this.localLimiteX1200, false);
          this.writeValue(this.input.customized.limite_volumen_200_X2, this.localLimiteX2200, false);

          // porcentaje de vega
          this.writeValue(this.input.customized.limite_volumen_vega_X1, this.localLimiteX1Vega, false);
          this.writeValue(this.input.customized.limite_volumen_vega_X2, this.localLimiteX2Vega, false);
        }

        this.editingValue = false;
      },

      writeValue(formattedAddress, value, isBool, description) {
        const rawAddress = this.vartable.find(v => v.formatted_address === formattedAddress)?.address;
        if (!rawAddress) return;
        let payload = null;
        if(isBool){
          payload = this.adaptFormat({
            type: "BIT",
            value: value
          });
        } else {
          const scaledValue = parseFloat(value) / this.input.customized.scale;
          payload = this.adaptFormat({
            type: this.input.customized.datatype,
            value: scaledValue
          });
        }

        this.doPublish({
          topic: this.write_topic_label || this.write_topic,
          message: { address: rawAddress, value: payload },
          description
        });
      },
  
      cancel() {
        this.editingValue = false;
      },
  
      adaptFormat(item) {
        return (() => {
          switch (item.type) {
            case "BIT":
              return item.value;
            case "ENTERO":
            case "REAL":
              return this.connection_type == "koiote-omron-connection"
                ? (() => {
                    const buffer = Buffer.allocUnsafe(4);
                    let valor = parseFloat(item.value);
                    buffer.writeFloatBE(valor);
                    let d = [];
                    d[0] = 256 * buffer[2] + buffer[3];
                    d[1] = 256 * buffer[0] + buffer[1];
                    return d;
                  })()
                : Number(item.value);
            default:
              return "";
          }
        })();
      },
  
      updateHeight() {
        const textElement = document.getElementById(this.labelId);
        const textElementHeight = textElement.getBoundingClientRect().height;
  
        const containerElement = textElement.parentElement?.parentElement;
        const containerElementHeight = containerElement?.getBoundingClientRect().height;
  
        if (textElementHeight > containerElementHeight) {
          containerElement.parentElement.style.height =
            this.input.customized.height * (textElementHeight / containerElementHeight || 1) + 2 + "%";
        }
      },
    },
  
    created() {
      this.labelId = this.generateId();
      window.addEventListener("resize", this.updateHeight);
      this.$nextTick(() => {
        this.updateHeight();
      });
    },
  
    beforeDestroy() {
      window.removeEventListener("resize", this.updateHeight);
    },
  };
  </script>
  
  <style scoped>
  .value-container {
    margin: 0;
    padding: 0;
  }
  
  .custom-cursor {
    cursor: pointer;
  }
  
  .KoioteValueLabelWrite {
    font-size: 1vw;
  }
  
  @media (orientation: portrait) {
    .KoioteValueLabelWrite {
      font-size: 1.3vw;
    }
  }
  
  @media (orientation: landscape) and (max-width: 600px) {
    .KoioteValueLabelWrite {
      font-size: 0.85vw;
    }
  }
  
  @media (orientation: portrait) and (max-width: 500px) {
    .KoioteValueLabelWrite {
      font-size: 1.55vw;
    }
  }
  .value-display {
    background-color: #f5f5f5;
    border: 1px dashed #999;
    cursor: pointer;
    text-align: center;
    font-weight: bold;
    transition: all 0.2s;
  }
  .value-display:hover {
    background-color: #e0e0e0;
    border-style: solid;
  }
</style>
  